import React, { useRef, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFacebook,
  faInstagram,
  faLinkedin,
  faTiktok,
  faWhatsapp,
} from '@fortawesome/free-brands-svg-icons';
import {
  faCalendarAlt,
  faBullseye,
  faLightbulb,
  faRocket,
  faUsers,
  faCogs,
  faBuilding,
  faCode,
  faChartLine,
  faGraduationCap,
  faQuoteRight,
  faShareAlt
} from '@fortawesome/free-solid-svg-icons';
import './About.css';
import { Link } from 'react-router-dom';

// Images
import historyImg from '../images/ATC_history.webp';
import emmanuelImg from '../images/emmanuel_458x500.webp';
import gilresImg from '../images/gilres_458x500.webp';
import mpeckImg from '../images/mpeck_458x500.webp';
import dafImg from '../images/DAF_ATC_450X500.webp';
import dgiImg from '../images/DGI_Achille_good_450x500.webp';
import dgcImg from '../images/DGC_ATC_450X500.webp';
import dgeImg from '../images/DGE_450x500.webp';
import dmcImg from '../images/DMC_450x500.webp';
import commercialImg from '../images/sav_458x500.jpeg';
import architectImg from '../images/architech_458x500.webp';
import heroBg from '../images/home_apropos.jpg_1920X750.webp'; 

const About = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  useEffect(() => {
    // Vérifier si l'URL contient le hash #allEquipe
    if (window.location.hash === '#allEquipe') {
      // Attendre que le DOM soit complètement chargé
      setTimeout(() => {
        const element = document.getElementById('allEquipe');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 500); // Délai pour laisser le temps au composant de se monter
    }
  }, []);
  
  const teamMembers = [
    {
      name: 'Emmanuel KAKOUANDÉ',
      role: t('fonction_ceo'),
      image: emmanuelImg,
      social: {
        whatsapp: 'https://wa.me/237681134804',
        instagram: 'https://www.instagram.com/emmanuel_kakouande',
        facebook: 'https://www.facebook.com/share/1B69YSSZNW/',
        tiktok: 'https://www.tiktok.com/@infogenie0',
      },
    },
    {
      name: 'Gilres TOGUEM',
      role: t('fonction_secretaire_general'),
      image: gilresImg,
      social: {
        whatsapp: 'https://wa.me/237697291057',
        facebook: 'https://www.facebook.com/profile.php?id=100069518353558',
        instagram: 'https://www.instagram.com/gilrestoguem',
        tiktok: 'https://www.tiktok.com/@gilres12doc',
      },
    },
    {
      name: 'Étienne MPECK',
      role: t('fonction_DRH'),
      image: mpeckImg,
      social: { facebook: '#', whatsapp: '#', linkedin: '#', tiktok: '#' },
    },
    {
      name: 'Floriane NKOA',
      role: t('fonction_DAF'),
      image: dafImg,
      social: { whatsapp: '#', facebook: '#', linkedin: '#', tiktok: '#' },
    },
    {
      name: 'Achille Tawokam',
      role: t('fonction_DGI'),
      image: dgiImg,
      social: {
        whatsapp: 'https://wa.me/237699388115',
        facebook: 'https://www.facebook.com/achille.kenmogne.9',
        linkedin: 'https://www.linkedin.com/in/achille-tawokam-1363602ab',
        tiktok: 'https://vm.tiktok.com/ZMHtV6LFAN8Ab-7vvYH/',
      },
    },
    {
      name: 'NDE KALA JUNIOR WILFRIED',
      role: t('fonction_DGC'),
      image: dgcImg,
      social: {
        whatsapp: 'https://wa.me/237695123990',
        facebook: 'https://www.facebook.com/share/16zfczToHo/',
        linkedin: 'https://www.linkedin.com/public-profile/settings',
        tiktok: 'https://www.tiktok.com/@massawilf',
      },
    },
    {
      name: 'Koagne William Mohamed',
      role: t('fonction_DGE'),
      image: dgeImg,
      social: {
        whatsapp: 'https://wa.me/237679417101',
        facebook: 'https://www.facebook.com/mohamed.njindjou',
        linkedin: 'https://www.linkedin.com/in/mohamed-koagne',
      },
    },
    {
      name: 'Ulrich Boutchouangb', 
      role: t('fonction_DMC'),
      image: dmcImg,
      social: {
        whatsapp: 'https://wa.me/237695264171',
        facebook: 'https://www.facebook.com/share/1F9cXh6Ybg/',
        tiktok: 'https://www.tiktok.com/@atccommunication2',
      },
    },
    {
      name: 'Delphine ENYEGUE',
      role: t('fonction_RC'),
      image: commercialImg,
      social: { 
        whatsapp: 'https://wa.me/33668383331', 
        facebook: 'https://www.facebook.com/share/19K6AjSHe5/?mibextid=wwXIfr', 
        tiktok: 'https://www.tiktok.com/@oekto_1?_r=1&_t=ZN-99QZfaEeNzK' },
    },
    {
      name: 'Legrand MPELE',
      role: t('fonction_Arch'),
      image: architectImg,
      social: { whatsapp: '#', facebook: '#', linkedin: '#', tiktok: '#' },
    },
  ];

  const objectives = [
    { icon: faBullseye, text: t('objectif1') },
    { icon: faRocket, text: t('objectif2') },
    { icon: faCogs, text: t('objectif3') },
    { icon: faUsers, text: t('objectif4') },
    { icon: faChartLine, text: t('objectif5') },
  ];

  const expertises = [
    { icon: faBuilding, label: t('expertise1'), color: '#0066cc', link: '/genie-electrique'},
    { icon: faCode, label: t('expertise2'), color: '#107a3e', link: '/genie-informatique' },
    { icon: faCogs, label: t('expertise3'), color: '#c8a02b', link: '/genie-civil'},
    { icon: faChartLine, label: t('expertise4'), color: '#6b3fa0', link: '/communication'},
    { icon: faGraduationCap, label: t('expertise5'), color: '#e67e22', link: '/formation'},
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.12, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] },
    },
  };

  const teamVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.06, delayChildren: 0.1 },
    },
  };

  const memberVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] },
    },
  };

  return (
    <div className="about-page">
      {/* ===== HERO ===== */}
      <section className="about-hero"   style={{ 
    backgroundImage: `url(${heroBg})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  }}>
        <div className="about-hero-overlay"></div>
        <div className="hero-particles">
          <span></span><span></span><span></span><span></span>
          <span></span><span></span><span></span><span></span>
        </div>
        <div className="container">
          <motion.div
            className="about-hero-content"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <motion.div
              className="hero-badge"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <span className="badge-dot"></span>
              {t('Cours_scolaire_Apropos')}
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              {t('histoire_titre')}
            </motion.h1>

            <motion.p
              className="hero-subtitle"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              {t('histoire_sous_titre')}
            </motion.p>

            <motion.div
              className="about-breadcrumb"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <span><a href="/">{t('menu_Accueil')}</a></span>
              <span className="separator">›</span>
              <span className="current">{t('Cours_scolaire_Apropos')}</span>
            </motion.div>

            <div className="hero-line-wrapper">
              <div className="hero-line"></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== HISTOIRE & APPROCHE ===== */}
      <section className="about-history" ref={ref}>
        <div className="container">
          <motion.div
            className="about-history-grid"
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
          >
            <motion.div className="about-history-image" variants={itemVariants}>
              <img src={historyImg} alt="ATC History" />
            </motion.div>

            <motion.div className="about-history-text" variants={itemVariants}>
              <div className="section-label">{t('histoire_titre')}</div>
              <h2>{t('histoire_sous_titre')}</h2>
              <div className="history-content">
                <p>{t('histoire_para1')}</p>
                <p>{t('histoire_para2')}</p>
                <div className="highlight-box">
                  <FontAwesomeIcon icon={faQuoteRight} className="quote-icon" />
                  <p>{t('histoire_para3')}</p>
                </div>
              </div>

              <div className="about-approach">
                <div className="section-label">{t('approche_titre')}</div>
                <h3>{t('approche_sous_titre')}</h3>
                <p>{t('approche_para1')}</p>
                <p>{t('approche_para2')}</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ===== OBJECTIFS & EXPERTISES ===== */}
      <section className="about-values">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="section-tag">Valeurs</span>
            <h2>Notre vision stratégique</h2>
          </motion.div>

          <motion.div
            className="about-values-grid"
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
          >
            {/* Objectifs */}
            <motion.div className="values-card" variants={itemVariants}>
              <div className="values-card-header">
                <FontAwesomeIcon icon={faBullseye} className="values-icon" />
                <h3>{t('objectifs_titre')}</h3>
              </div>
              <ul className="values-list">
                {objectives.map((obj, index) => (
                  <li key={index}>
                    <span className="li-number">{String(index + 1).padStart(2, '0')}</span>
                    <span className="li-text">{obj.text}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Expertises */}
            <motion.div className="values-card" variants={itemVariants}>
              <div classNme="values-card-header">
                <FontAwesomeIcon icon={faLightbulb} className="values-icon" />
                <h3>{t('expertise_titre')}</h3>
              </div>
              <div className="expertise-grid">
                {expertises.map((exp, index) => (
                  <Link 
                    key={index} 
                    to={exp.link} 
                    className="expertise-item" 
                    style={{ borderColor: exp.color }}
                  >
                    <div className="expertise-icon" style={{ backgroundColor: exp.color + '15', color: exp.color }}>
                      <FontAwesomeIcon icon={exp.icon} />
                    </div>
                    <span>{exp.label}</span>
                    
                  </Link>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ===== RÉSEAUX & DATE ===== */}
      <section className="about-meta">
        <div className="container">
          <motion.div
            className="about-meta-grid"
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
          >
            <motion.div className="meta-card" variants={itemVariants}>
              <div className="meta-card-icon">
                <FontAwesomeIcon icon={faShareAlt} />
              </div>
              <h3>{t('reseaux_titre')}</h3>
              <div className="meta-social">
                <a href="https://www.facebook.com/profile.php?id=100080652765508" target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faFacebook} />
                  <span>Facebook</span>
                </a>
                <a href="https://www.tiktok.com/@atc_hub_techpreneuriat?_r=1&_t=ZS-99QD1gHuP3d" target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faTiktok} />
                  <span>TikTok</span>
                </a>
                <a href="https://www.instagram.com/atchhub237?igsi=ejB6c2tiZHl3MnAw" target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faInstagram} />
                  <span>Instagram</span>
                </a>
                <a href="https://www.linkedin.com/company/118613966/admin/dashboard/" target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faLinkedin} />
                  <span>LinkedIn</span>
                </a>
              </div>
            </motion.div>

            <motion.div className="meta-card" variants={itemVariants}>
              <div className="meta-card-icon">
                <FontAwesomeIcon icon={faCalendarAlt} />
              </div>
              <h3>{t('date_creation')}</h3>
              <div className="meta-date">
                <span className="date-day">2024</span>
                <span className="date-month">{t('juin_2024')}</span>
              </div>
              <div className="meta-badge">Fondation</div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ===== ÉQUIPE ===== */}
      <section className="about-team" id="allEquipe">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="section-tag">Leadership</span>
            <h2>{t('titre_Notre_equippe')}</h2>
            <p>{t('msg1_Notre_equippe')}</p>
          </motion.div>

          <motion.div
            className="about-team-grid"
            variants={teamVariants}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
          >
            {teamMembers.map((member, index) => (
              <motion.div
                key={index}
                className="team-card"
                variants={memberVariants}
                whileHover={{ y: -8 }}
                transition={{ duration: 0.3 }}
              >
                <div className="team-card-image">
                  <img src={member.image} alt={member.name}/>
                </div>
                <div className="team-card-info">
                  <h5>{member.name}</h5>
                  <span>{member.role}</span>
                  <div className="team-social">
                    {member.social.whatsapp && (
                      <a href={member.social.whatsapp} target="_blank" rel="noopener noreferrer">
                        <FontAwesomeIcon icon={faWhatsapp} />
                      </a>
                    )}
                    {member.social.facebook && (
                      <a href={member.social.facebook} target="_blank" rel="noopener noreferrer">
                        <FontAwesomeIcon icon={faFacebook} />
                      </a>
                    )}
                    {member.social.instagram && (
                      <a href={member.social.instagram} target="_blank" rel="noopener noreferrer">
                        <FontAwesomeIcon icon={faInstagram} />
                      </a>
                    )}
                    {member.social.linkedin && (
                      <a href={member.social.linkedin} target="_blank" rel="noopener noreferrer">
                        <FontAwesomeIcon icon={faLinkedin} />
                      </a>
                    )}
                    {member.social.tiktok && (
                      <a href={member.social.tiktok} target="_blank" rel="noopener noreferrer">
                        <FontAwesomeIcon icon={faTiktok} />
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;